/**
 * Phase 169 — per-role receipt visibility (set by the municipal admin).
 *
 *   const rv = useReceiptVisibility(code);        // effective state for the signed-in user
 *   rv.visibleNow                                  // may THIS role open receipts today at all?
 *   rv.canView(payment_date)                       // ... and this particular receipt (payment-date gate)?
 *   rv.message                                     // the municipality's explanation when hidden
 *
 * Municipal admins / super admins are never governed (canView() is always true). The hook is cached per tenant for
 * the lifetime of the tab and refreshed every REFRESH_MS so an admin change applies without a reload. The backend
 * enforces the very same rule on every receipt endpoint — this only decides whether to show a link / navigate.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "./api";

const REFRESH_MS = 60000;
const cache = {};   // code -> { state, at, promise }

export const OPEN_STATE = { governed: false, visible_now: true, payment_gated: false, payment_from: null, payment_to: null, reason: null, message: null };

function fetchState(code, force = false) {
  const c = cache[code] || (cache[code] = { state: null, at: 0, promise: null });
  if (!force && c.state && Date.now() - c.at < REFRESH_MS) return Promise.resolve(c.state);
  if (c.promise) return c.promise;
  c.promise = api.get(`/t/${code}/receipt-visibility/me`).then((r) => {
    c.state = r.data || OPEN_STATE;
    c.at = Date.now();
    return c.state;
  }).catch(() => c.state || OPEN_STATE).finally(() => { c.promise = null; });
  return c.promise;
}

/** Pure helper (also used with list rows that carry `receipt_visible` from the server). */
export function receiptAllowed(state, paymentDate) {
  if (!state || !state.governed) return true;
  if (!state.visible_now) return false;
  if (!state.payment_gated) return true;
  const d = String(paymentDate || "").slice(0, 10);
  if (!d) return false;
  if (state.payment_from && d < state.payment_from) return false;
  if (state.payment_to && d > state.payment_to) return false;
  return true;
}

export function useReceiptVisibility(code, { enabled = true } = {}) {
  const [state, setState] = useState(() => (code && cache[code]?.state) || null);
  const [loading, setLoading] = useState(!state);
  const refresh = useCallback(() => {
    if (!code || !enabled) return Promise.resolve(OPEN_STATE);
    return fetchState(code, true).then((s) => { setState(s); return s; });
  }, [code, enabled]);
  useEffect(() => {
    if (!code || !enabled) { setLoading(false); return undefined; }
    let alive = true;
    setLoading(!cache[code]?.state);
    fetchState(code).then((s) => { if (alive) { setState(s); setLoading(false); } });
    const t = setInterval(() => fetchState(code).then((s) => { if (alive) setState(s); }), REFRESH_MS);
    return () => { alive = false; clearInterval(t); };
  }, [code, enabled]);
  const s = state || OPEN_STATE;
  return {
    loading, state: s, refresh,
    governed: !!s.governed,
    visibleNow: s.visible_now !== false,
    paymentGated: !!s.payment_gated,
    message: s.message || null,
    reason: s.reason || null,
    canView: (paymentDate) => receiptAllowed(s, paymentDate),
  };
}

/** Drop the cached state (e.g. right after the admin saved new rules in this tab). */
export function invalidateReceiptVisibility(code) {
  if (code) delete cache[code];
}
